/**
 * Per-country phone rules — MIRRORS apps/api/src/lib/phone-rules.ts. Keep the two IDENTICAL
 * (a guard test compares them; the API is the gate, this is the instant feedback).
 *
 * The country is always chosen from a dropdown, so the national number's length can be checked
 * against that country rather than against one loose worldwide range. The previous rule accepted
 * any 8–15 digits, which let a half-typed UK number through: "+44 791112" passed.
 *
 * `nsn` = allowed lengths of the National Significant Number — the digits AFTER the country code
 * and WITHOUT the trunk "0" (a UK number is entered 7911 123456, not 07911 123456).
 *
 * Ranges rather than a single number where a country genuinely uses several lengths. Rejecting a
 * client's real number is worse than accepting one odd one, so where a country is mixed the list
 * is generous. The United Kingdom is pinned to 10 on Raj's instruction (2026-10-06).
 */

export interface Country {
  code: string;
  dial: string; // dial code without "+"
  flag: string; // emoji
  name: string;
  /** Allowed national-significant-number lengths for this country. */
  nsn: number[];
}

export const COUNTRIES: Country[] = [
  { code: 'GB', dial: '44',  flag: '🇬🇧', name: 'United Kingdom',       nsn: [10] },
  { code: 'IE', dial: '353', flag: '🇮🇪', name: 'Ireland',              nsn: [7, 8, 9] },
  { code: 'US', dial: '1',   flag: '🇺🇸', name: 'United States',        nsn: [10] },
  { code: 'CA', dial: '1',   flag: '🇨🇦', name: 'Canada',               nsn: [10] },
  { code: 'IN', dial: '91',  flag: '🇮🇳', name: 'India',                nsn: [10] },
  { code: 'AU', dial: '61',  flag: '🇦🇺', name: 'Australia',            nsn: [9] },
  { code: 'NZ', dial: '64',  flag: '🇳🇿', name: 'New Zealand',          nsn: [8, 9, 10] },
  { code: 'FR', dial: '33',  flag: '🇫🇷', name: 'France',               nsn: [9] },
  { code: 'DE', dial: '49',  flag: '🇩🇪', name: 'Germany',              nsn: [7, 8, 9, 10, 11] },
  { code: 'ES', dial: '34',  flag: '🇪🇸', name: 'Spain',                nsn: [9] },
  { code: 'IT', dial: '39',  flag: '🇮🇹', name: 'Italy',                nsn: [8, 9, 10, 11] },
  { code: 'NL', dial: '31',  flag: '🇳🇱', name: 'Netherlands',          nsn: [9] },
  { code: 'BE', dial: '32',  flag: '🇧🇪', name: 'Belgium',              nsn: [8, 9] },
  { code: 'CH', dial: '41',  flag: '🇨🇭', name: 'Switzerland',          nsn: [9] },
  { code: 'PT', dial: '351', flag: '🇵🇹', name: 'Portugal',             nsn: [9] },
  { code: 'SE', dial: '46',  flag: '🇸🇪', name: 'Sweden',               nsn: [7, 8, 9] },
  { code: 'NO', dial: '47',  flag: '🇳🇴', name: 'Norway',               nsn: [8] },
  { code: 'DK', dial: '45',  flag: '🇩🇰', name: 'Denmark',              nsn: [8] },
  { code: 'PL', dial: '48',  flag: '🇵🇱', name: 'Poland',               nsn: [9] },
  { code: 'AE', dial: '971', flag: '🇦🇪', name: 'United Arab Emirates', nsn: [8, 9] },
  { code: 'SA', dial: '966', flag: '🇸🇦', name: 'Saudi Arabia',         nsn: [9] },
  { code: 'QA', dial: '974', flag: '🇶🇦', name: 'Qatar',                nsn: [8] },
  { code: 'SG', dial: '65',  flag: '🇸🇬', name: 'Singapore',            nsn: [8] },
  { code: 'HK', dial: '852', flag: '🇭🇰', name: 'Hong Kong',            nsn: [8] },
  { code: 'ZA', dial: '27',  flag: '🇿🇦', name: 'South Africa',         nsn: [9] },
  { code: 'NG', dial: '234', flag: '🇳🇬', name: 'Nigeria',              nsn: [7, 8, 9, 10] },
  { code: 'KE', dial: '254', flag: '🇰🇪', name: 'Kenya',                nsn: [9] },
  { code: 'PK', dial: '92',  flag: '🇵🇰', name: 'Pakistan',             nsn: [10] },
  { code: 'BD', dial: '880', flag: '🇧🇩', name: 'Bangladesh',           nsn: [9, 10] },
  { code: 'CN', dial: '86',  flag: '🇨🇳', name: 'China',                nsn: [10, 11] },
  { code: 'JP', dial: '81',  flag: '🇯🇵', name: 'Japan',                nsn: [9, 10] },
];

export const DEFAULT_DIAL = '44';

/** Longest dial codes first so "+44" beats "+4" when matching a prefix. */
export const DIALS_DESC = [...new Set(COUNTRIES.map((c) => c.dial))].sort(
  (a, b) => b.length - a.length,
);

/**
 * Split a stored value ("+44 7911123456") into its dial code and national number.
 *
 * `known` is false when the value carried a country code we do not list. The input still
 * shows the default country, but validation must NOT then measure the number against the
 * United Kingdom — "+352 621234567" (Luxembourg) is not a malformed UK number.
 */
export function splitPhone(value: string): { dial: string; national: string; known: boolean } {
  if (!value) return { dial: DEFAULT_DIAL, national: '', known: true };
  const v = value.replace(/[^\d+]/g, '');
  if (v.startsWith('+')) {
    const digits = v.slice(1);
    const dial = DIALS_DESC.find((d) => digits.startsWith(d));
    if (dial) return { dial, national: digits.slice(dial.length), known: true };
    return { dial: DEFAULT_DIAL, national: digits, known: false };
  }
  return { dial: DEFAULT_DIAL, national: v, known: true };
}

/** Allowed national lengths for a dial code. Several countries share one (+1 → US/CA). */
export function nsnLengthsFor(dial: string): number[] {
  const lengths = COUNTRIES.filter((c) => c.dial === dial).flatMap((c) => c.nsn);
  return [...new Set(lengths)].sort((a, b) => a - b);
}

/** "10 digits", or "8, 9 or 10 digits" when a country allows several. */
export function expectedLengthLabel(dial: string): string {
  const l = nsnLengthsFor(dial);
  if (l.length === 0) return 'a valid number';
  if (l.length === 1) return `${l[0]} digits`;
  return `${l.slice(0, -1).join(', ')} or ${l[l.length - 1]} digits`;
}

/**
 * Is this a complete number for the country its dial code names?
 * Empty is NOT valid here — "required" is a separate decision each form makes.
 */
export function isValidPhoneNumber(value: string): boolean {
  const { dial, national, known } = splitPhone(value);
  if (!national) return false;
  // An unlisted country falls back to the old permissive range rather than blocking a
  // real number from a country we simply have not added to the table.
  if (!known) return national.length >= 8 && national.length <= 15;
  const lengths = nsnLengthsFor(dial);
  if (lengths.length === 0) return national.length >= 8 && national.length <= 15;
  // A leading 0 is the trunk prefix and is never part of the national significant number.
  if (national.startsWith('0')) return false;
  return lengths.includes(national.length);
}

/**
 * The message shown under the field when a number is the wrong length.
 *
 * Deliberately plain: it states the required length and nothing else. Naming the country and
 * the dial code made the line long without telling the user anything they could not already
 * see in the dropdown next to the field.
 */
export function phoneLengthError(value: string): string | null {
  const { dial, national, known } = splitPhone(value);
  if (!national) return null;
  if (!known) return isValidPhoneNumber(value) ? null : 'Enter a valid phone number including country code';
  if (national.startsWith('0')) return 'Enter the number without the leading 0';
  if (isValidPhoneNumber(value)) return null;
  return `Number must be ${expectedLengthLabel(dial)}`;
}
